import type { ReactNode } from "react";
import { cn } from "@/shared/lib/cn";
import { FormGrid, type FormColumns } from "./FormGrid";

export interface FormSectionProps {
  title: string;
  description?: string;
  children: ReactNode;
  /** Contenido a la derecha del título (pestañas de idioma, un aviso…). */
  aside?: ReactNode;
  /** Campos por fila en pantalla ancha. */
  columns?: FormColumns;
  /**
   * Aprieta el relleno y el encabezado. Es para los formularios que se
   * recorren por pasos y quieren caber en una vista: ahí cada bloque
   * paga su marco dos veces, arriba y abajo, y con tres bloques en el
   * paso esos márgenes son ya un tercio de la pantalla.
   */
  compact?: boolean;
  className?: string;
}

/**
 * Un bloque del formulario: título, explicación y su rejilla de campos.
 *
 * Ocupa SIEMPRE el ancho entero y se apila con sus hermanos: dos
 * tarjetas nunca van en paralelo (Anahí, 26-ago). Lo que se reparte en
 * columnas son los CAMPOS de dentro, hasta tres. Ver `FormSectionStack`.
 *
 * Es `<fieldset>` con `<legend>` y no un `<div>` con un `<h2>`: así un
 * lector de pantalla anuncia "Precios" al entrar en CADA campo del
 * bloque, y "MXN" deja de ser un campo sin contexto.
 *
 * La rejilla ya no se escribe aquí: la pone `FormGrid`, que es donde vive
 * la única escalera de columnas del panel. Un campo que necesite la fila
 * entera se envuelve en `FormRow`, no en un `col-span` a mano.
 *
 * Candidata a `shared/ui/` — el formulario largo del producto no puede
 * ser el único de la app que necesite bloques.
 */
export function FormSection({
  title,
  description,
  children,
  aside,
  columns = 2,
  compact = false,
  className,
}: FormSectionProps) {
  return (
    <fieldset
      className={cn(
        "rounded-2xl border border-ds-border bg-ds-surface",
        compact ? "p-4 sm:p-5" : "p-5 sm:p-6",
        className,
      )}
    >
      {/*
        `<legend>` va como PRIMER hijo directo del `<fieldset>`: es la
        única posición en la que cuenta como nombre accesible del grupo.
        Metido dentro de un `<div>` se vuelve un texto cualquiera y los
        campos se quedan sin el contexto que este bloque les daba.
      */}
      {/*
        `float-left` saca al `<legend>` de su hueco en el borde del
        `<fieldset>` para que se lea como el título de una tarjeta y no
        como una etiqueta cosida al marco.

        Y `clear-both` en lo que viene detrás es la otra mitad: sin él, la
        descripción se coloca AL LADO del título flotado y arranca
        sangrada, como el texto que rodea una foto de periódico. Ponerle
        `w-full` al flotante no sirve —entonces no queda hueco a su lado y
        el texto se estruja en una columna de dos palabras—: lo que hace
        falta es no ponerse al lado, que es exactamente lo que `clear`
        significa.
      */}
      <legend className="float-left text-[15px] font-semibold text-ds-ink">
        {title}
      </legend>

      {/*
        El `aside` va en la MISMA fila que el título, no en la de la
        explicación (Anahí, 26-ago). Flota a la derecha contra el título
        flotado a la izquierda, así que los dos comparten renglón y la
        explicación arranca debajo de los dos, a la izquierda del todo.

        Se pinta DESPUÉS del `<legend>` porque el `<legend>` tiene que
        ser el primer hijo del `<fieldset>` para contar como nombre
        accesible del grupo; el orden visual lo resuelve el flotado.
      */}
      {aside && <div className="float-right -mt-1">{aside}</div>}

      <div
        className={cn(
          "clear-both",
          compact ? "mb-4 pt-2.5" : "mb-5 pt-3",
        )}
      >
        {description && (
          /*
            Sin `max-w-prose`: con el tope de 65 caracteres, una
            explicación de dos frases partía en dos renglones aunque el
            bloque ocupara la pantalla entera, y en un formulario que
            pelea por caber cada renglón cuenta (Anahí, 26-ago). Ahora
            que el `aside` subió al título, la explicación tiene la fila
            entera para ella.
          */
          <p className="text-[13px] leading-relaxed text-ds-ink-3">{description}</p>
        )}
      </div>

      <FormGrid columns={columns}>{children}</FormGrid>
    </fieldset>
  );
}
